import { AIQuestionGenerator } from "@/components/candidates/evaluation/AIQuestionGenerator";
import { CandidateAssessmentAnswers } from "@/components/candidates/evaluation/CandidateAssessmentAnswers";
import { CandidateBasicInfo } from "@/components/candidates/evaluation/CandidateBasicInfo";
import { CandidateEducationInfo } from "@/components/candidates/evaluation/CandidateEducationInfo";
import { CandidateEvaluationForm } from "@/components/candidates/evaluation/CandidateEvaluationForm";
import { CandidateStatusUpdateForm } from "@/components/candidates/evaluation/CandidateStatusUpdateForm";
import { CandidateEvaluationHistory } from "@/components/candidates/evaluation/CandidateEvaluationHistory";
import { CandidateExperienceInfo } from "@/components/candidates/evaluation/CandidateExperienceInfo";
import { CandidateSkillsInfo } from "@/components/candidates/evaluation/CandidateSkillsInfo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SendOfferLetterDialog } from "@/components/candidates/evaluation/SendOfferLetterDialog";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { formatCandidateStatus, formatEvaluationRound } from "@/utils/formatters";
import { getCandidateStatusColor } from "@/utils/statusColors";
import { CANDIDATE_STATUS } from "@/types/enums";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthorization } from "@/hooks/useAuthorization";
import { CandidateCtcNoteForm } from "@/components/candidates/evaluation/CandidateCtcNoteForm";
import { USER_ROLES } from "@/types/enums";

type Candidate = Omit<Tables<"candidates">, 'interviewer'> & { joining_date?: string | null; interviewer?: string[] | string | null };

export default function CandidateEvaluationPage() {
  const { candidateId } = useParams<{ candidateId: string }>();
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);
  const { role } = useAuthorization();

  const { data: candidate, isLoading } = useQuery({
    queryKey: ["candidate", candidateId, refreshKey],
    queryFn: async () => {
      if (!candidateId) throw new Error("Candidate ID is required");

      const { data, error } = await supabase
        .from("candidates")
        .select(
          `
          *,
          job_positions:job_position_id(role_name,job_type),
          colleges:college_id(name)
        `
        )
        .eq("id", candidateId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!candidateId,
  });

  const handleEvaluationComplete = () => {
    setRefreshKey((prev) => prev + 1);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center">Loading candidate details...</div>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="space-y-6">
        <div className="text-center">Candidate not found</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          onClick={() => navigate("/admin/candidates")}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Candidates
        </Button>
        <div className="flex-1 text-center">
          <p className="text-muted-foreground">Candidate Evaluation</p>
          <h1 className="text-3xl font-bold">{candidate.full_name}</h1>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
            <Badge className={getCandidateStatusColor(candidate.status)}>
              {formatCandidateStatus(candidate.status)}
            </Badge>
            <Badge variant="outline">
              {candidate.current_round
                ? formatEvaluationRound(candidate.current_round)
                : "Not Started"}
            </Badge>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Left Column - Candidate Information */}
        <div className="space-y-6">
          <CandidateBasicInfo candidate={candidate} />
          <CandidateEducationInfo candidate={candidate} />
          <CandidateExperienceInfo candidate={candidate} />
          <CandidateSkillsInfo candidate={candidate} />
          <CandidateAssessmentAnswers candidateId={candidateId!} />
        </div>

        {/* Right Column - Evaluation */}
        <div className="space-y-6">
          <AIQuestionGenerator candidate={candidate} />
          <CandidateEvaluationForm
            candidate={candidate}
            onEvaluationComplete={handleEvaluationComplete}
          />
          <CandidateStatusUpdateForm
            candidate={candidate}
            onSaved={handleEvaluationComplete}
          />
          {(role === USER_ROLES.ADMIN || role === USER_ROLES.HR) && (
            <CandidateCtcNoteForm
              candidate={candidate}
              onSaved={handleEvaluationComplete}
            />
          )}
          {candidate.status === CANDIDATE_STATUS.SELECTED && (
            <SendOfferLetterDialog
              candidate={candidate}
              onSent={handleEvaluationComplete}
            />
          )}
          <CandidateEvaluationHistory
            candidateId={candidateId!}
            refreshKey={refreshKey}
            onReevaluationComplete={handleEvaluationComplete}
          />
        </div>
      </div>
    </div>
  );
}
